// src/pages/employer/EmployerScreening.jsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { screeningAPI } from '../services/screeningAPI';
import { formatAPIError } from '../utils/validation';
import Header from '../components/layout/Header';
import './styles/EmployerScreening.css'

const EmployerScreening = () => {
  const { applicationId } = useParams();
  const { user, token, logout, isEmployer } = useAuth(); const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // action form
  const [action, setAction] = useState('');
  const [actionNotes, setActionNotes] = useState('');

  // interview form
  const [interview, setInterview] = useState({
    date: '',
    time: '',
    mode: 'ONLINE',
    location: '',
    interviewerName: '',
    interviewerEmail: '',
    notes: '',
  });

  // final outcome form
  const [outcome, setOutcome] = useState('');
  const [outcomeNotes, setOutcomeNotes] = useState('');

  useEffect(() => {
  if (!isEmployer) {
    navigate('/dashboard');
    return;
  }

  if (!token) return;

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await screeningAPI.getCandidateProfile(applicationId, token);
      setProfile(res.data);
    } catch (err) {
      setError(formatAPIError(err) || 'Failed to load screening profile');
    } finally {
      setLoading(false);
    }
  };

  load();
}, [applicationId, isEmployer, navigate, token]);


  const handleExportPdf = async () => {
    try {
      const res = await screeningAPI.exportProfilePdf(applicationId, token);
      const blob = new Blob([res.data], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `screening-${applicationId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatAPIError(err) || 'Failed to export PDF');
    }
  };

  const handleSubmitAction = async (e) => {
    e.preventDefault();
    if (!action) return;

    try {
      setError('');
      setSuccess('');
      await screeningAPI.takeAction(
        applicationId,
        { action, notes: actionNotes },
        token
      );
      setSuccess('Screening action saved successfully.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(formatAPIError(err) || 'Failed to save screening action');
    }
  };

  const handleScheduleInterview = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      const payload = {
        interviewDate: interview.date,
        interviewTime: interview.time,
        mode: interview.mode,
        location: interview.location || '',
        interviewerName: interview.interviewerName || `${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`.trim(),
        interviewerEmail: interview.interviewerEmail || user?.email || '',
        notes: interview.notes || '',
      };


      await screeningAPI.scheduleInterview(applicationId, payload, token);
      setSuccess('Interview scheduled successfully.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(formatAPIError(err) || 'Failed to schedule interview');
    }
  };

  const handleSubmitOutcome = async (e) => {
    e.preventDefault();
    if (!outcome) return;
    try {
      setError('');
      setSuccess('');
      await screeningAPI.recordFinalOutcome(
        applicationId,
        { outcome, notes: outcomeNotes },
        token
      );
      setSuccess('Final outcome recorded.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(formatAPIError(err) || 'Failed to record outcome');
    }
  };

  if (!user) return null;

  return (
    <>
      <Header user={user} onLogout={logout} />

      <div className="screening-dashboard-container">
        <div className="dashboard-content">
          {/* Page header */}
          <div className="screening-dashboard-card" style={{ marginBottom: '1.5rem' }}>
            <div className="screening-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                className="btn-secondary"
                onClick={() => navigate(-1)}
              >
                ← Back
              </button>
              <div>
                <h2>Candidate Screening</h2>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                  Review verified profile, take screening actions, and manage interviews.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn-primary"
                  onClick={handleExportPdf}
                  disabled={loading}
                >
                  Export PDF
                </button>
              </div>
            </div>

            {(error || success) && (
              <div style={{ marginTop: '1rem' }}>
                {error && (
                  <div className="alert-error">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="alert-success">
                    {success}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Loading / Empty */}
          {loading && (
            <div className="screening-dashboard-card">
              <p>Loading screening profile...</p>
            </div>
          )}

          {!loading && !profile && (
            <div className="screening-dashboard-card">
              <p>Screening profile not available for this application.</p>
            </div>
          )}

          {!loading && profile && (
            <>
              {/* Candidate summary */}
              <div className="screening-dashboard-card" style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <h3 style={{ marginBottom: '0.25rem' }}>{profile.candidate.name}</h3>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                      {profile.candidate.headline || 'Candidate'}
                    </p>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                      {profile.candidate.location} · {profile.candidate.email}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '0.875rem', color: '#6b7280' }}>
                    <div><strong>Application:</strong> {profile.application.id}</div>
                    <div><strong>Status:</strong> {profile.application.status}</div>
                    <div>
                      <strong>Applied:</strong>{' '}
                      {new Date(profile.application.appliedDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Verification summary */}
              <div className="screening-dashboard-card" style={{ marginBottom: '1.5rem' }}>
                <h3>Verification Summary</h3>
                <div className="screening-stats-grid">
                  <div className="screening-stat-card">
                    <div className="screening-stat-label">Education</div>
                    <div className="screening-stat-value">
                      {profile.verificationSummary.education.verified} /{' '}
                      {profile.verificationSummary.education.total}
                    </div>
                  </div>
                  <div className="screening-stat-card">
                    <div className="screening-stat-label">Employment</div>
                    <div className="screening-stat-value">
                      {profile.verificationSummary.employment.verified} /{' '}
                      {profile.verificationSummary.employment.total}
                    </div>
                  </div>
                  <div className="screening-stat-card">
                    <div className="screening-stat-label">Certifications</div>
                    <div className="screening-stat-value">
                      {profile.verificationSummary.certifications.verified} /{' '}
                      {profile.verificationSummary.certifications.total}
                    </div>
                  </div>
                  <div className="screening-stat-card">
                    <div className="screening-stat-label">Background Checks</div>
                    <div className="screening-stat-value">
                      {profile.verificationSummary.background.completed} /{' '}
                      {profile.verificationSummary.background.total}
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Application Status*/}
              <div className="screening-dashboard-card" style={{ marginBottom: '1.5rem' }}>
                <h3>Current Application Status</h3>
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid #3b82f6'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '1.125rem', marginBottom: '0.25rem' }}>
                        {profile.application.status === 'SHORTLISTED' && '✓ Shortlisted for Interview'}
                        {profile.application.status === 'REJECTED' && '✗ Rejected'}
                        {profile.application.status === 'UNDER_REVIEW' && '⏸ Under Review'}
                        {profile.application.status === 'ACCEPTED' && '🎉 Accepted/Hired'}
                        {profile.application.status === 'APPLIED' && '📋 Application Submitted'}
                        {!['SHORTLISTED', 'REJECTED', 'UNDER_REVIEW', 'ACCEPTED', 'APPLIED'].includes(profile.application.status) &&
                          `Status: ${profile.application.status}`}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {profile.application.status === 'SHORTLISTED' && 'Candidate has been selected for interview'}
                        {profile.application.status === 'REJECTED' && 'Application was rejected'}
                        {profile.application.status === 'UNDER_REVIEW' && 'Application is on hold for review'}
                        {profile.application.status === 'ACCEPTED' && 'Final decision: Hired'}
                        {profile.application.status === 'APPLIED' && 'No screening action taken yet'}
                      </div>
                    </div>
                    <div style={{
                      fontSize: '0.875rem',
                      color: '#fff',
                      backgroundColor:
                        profile.application.status === 'ACCEPTED' ? '#10b981' :
                          profile.application.status === 'SHORTLISTED' ? '#3b82f6' :
                            profile.application.status === 'REJECTED' ? '#ef4444' :
                              profile.application.status === 'UNDER_REVIEW' ? '#f59e0b' :
                                '#6b7280',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.375rem',
                      fontWeight: 500
                    }}>
                      {profile.application.status}
                    </div>
                  </div>
                </div>

                {/* Warning if final outcome already recorded */}
                {(profile.application.status === 'ACCEPTED' || profile.application.status === 'REJECTED') && (
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '0.75rem',
                    backgroundColor: '#fef3c7',
                    borderLeft: '4px solid #f59e0b',
                    fontSize: '0.875rem',
                    color: '#92400e'
                  }}>
                    ⚠️ Final outcome has already been recorded for this application.
                  </div>
                )}
              </div>

              {/* Verified sections */}
              <div className="screening-two-column-grid" style={{ gap: '1.5rem' }}>
                <div className="screening-dashboard-card">
                  <h3>Verified Education</h3>
                  {profile.education.length === 0 ? (
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      No verified education records.
                    </p>
                  ) : (
                    profile.education.map((edu) => (
                      <div key={edu.id} className="list-item">
                        <div style={{ fontWeight: 500 }}>
                          {edu.degree} · {edu.institution}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          {edu.verification?.status || 'VALIDATED'}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="screening-dashboard-card">
                  <h3>Verified Employment</h3>
                  {profile.employment.length === 0 ? (
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      No verified employment records.
                    </p>
                  ) : (
                    profile.employment.map((emp) => (
                      <div key={emp.id} className="screening-list-item">
                        <div style={{ fontWeight: 500 }}>
                          {emp.jobTitle} · {emp.company}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          {emp.verification?.status || 'VALIDATED'}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="screening-two-column-grid" style={{ marginTop: '1.5rem', gap: '1.5rem' }}>
                <div className="screening-dashboard-card">
                  <h3>Verified Certifications</h3>
                  {profile.certifications.length === 0 ? (
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      No verified certifications.
                    </p>
                  ) : (
                    profile.certifications.map((cert) => (
                      <div key={cert.id} className="screening-list-item">
                        <div style={{ fontWeight: 500 }}>
                          {cert.name} · {cert.issuer}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          {cert.verification?.status || 'VALIDATED'}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="screening-dashboard-card">
                  <h3>Background Checks</h3>
                  {profile.backgroundChecks.length === 0 ? (
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      No completed background checks.
                    </p>
                  ) : (
                    profile.backgroundChecks.map((bc) => (
                      <div key={bc.id} className="screening-list-item">
                        <div style={{ fontWeight: 500 }}>
                          {bc.backgroundCheck?.agency?.profile?.firstName}{' '}
                          {bc.backgroundCheck?.agency?.profile?.lastName}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          {bc.backgroundCheck?.result || bc.status}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Actions + interview + outcome */}
              <div className="screening-two-column-grid" style={{ marginTop: '1.5rem', gap: '1.5rem' }}>
                {/* Screening action */}
                <div className="screening-dashboard-card">
                  <h3>Screening Action</h3>
                  <form onSubmit={handleSubmitAction} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{ flex: 1 }}>
                      <div className="screening-form-group">
                        <label className="screening-form-label">Decision *</label>
                        <select
                          className="screening-form-control"
                          value={action}
                          onChange={(e) => setAction(e.target.value)}
                          required
                        >
                          <option value="">Choose...</option>
                          <option value="SELECT_FOR_INTERVIEW">Select for Interview</option>
                          <option value="REJECT">Reject</option>
                          <option value="HOLD">Hold</option>
                        </select>
                      </div>
                      <div className="screening-form-group">
                        <label className="screening-form-label">Notes</label>
                        <textarea
                          className="screening-form-control"
                          rows={3}
                          value={actionNotes}
                          onChange={(e) => setActionNotes(e.target.value)}
                        />
                      </div>
                    </div>
                    <button className="btn-primary" type="submit" style={{ marginTop: 'auto' }}>
                      Save Action
                    </button>
                  </form>
                </div>

                {/* Interview scheduling */}
                <div className="screening-dashboard-card">
                  <h3>Schedule Interview</h3>
                  <form onSubmit={handleScheduleInterview} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{ flex: 1 }}>
                      <div className="screening-form-row" style={{ display: 'flex', gap: '0.75rem' }}>
                        <div className="screening-form-group" style={{ flex: 1 }}>
                          <label className="screening-form-label">Date *</label>
                          <input
                            type="date"
                            className="screening-form-control"
                            value={interview.date}
                            onChange={(e) =>
                              setInterview((prev) => ({ ...prev, date: e.target.value }))
                            }
                            required
                          />
                        </div>
                        <div className="screening-form-group" style={{ flex: 1 }}>
                          <label className="screening-form-label">Time *</label>
                          <input
                            type="time"
                            className="screening-form-control"
                            value={interview.time}
                            onChange={(e) =>
                              setInterview((prev) => ({ ...prev, time: e.target.value }))
                            }
                            required
                          />
                        </div>
                      </div>
                      <div className="screening-form-group">
                        <label className="screening-form-label">Mode *</label>
                        <select
                          className="screening-form-control"
                          value={interview.mode}
                          onChange={(e) =>
                            setInterview((prev) => ({ ...prev, mode: e.target.value }))
                          }
                          required
                        >
                          <option value="ONLINE">Online</option>
                          <option value="ONSITE">On-site</option>
                        </select>
                      </div>
                      <div className="screening-form-group">
                        <label className="screening-form-label">
                          {interview.mode === 'ONLINE' ? 'Meeting Link' : 'Location'}
                        </label>
                        <input
                          type="text"
                          className="screening-form-control"
                          placeholder={interview.mode === 'ONLINE' ? 'https://meet.google.com/xyz' : 'Office address'}
                          value={interview.location}
                          onChange={(e) =>
                            setInterview((prev) => ({ ...prev, location: e.target.value }))
                          }
                        />
                      </div>
                      <div className="screening-form-row" style={{ display: 'flex', gap: '0.75rem' }}>
                        <div className="screening-form-group" style={{ flex: 1 }}>
                          <label className="screening-form-label">Interviewer Name</label>
                          <input
                            type="text"
                            className="screening-form-control"
                            placeholder="Who will conduct the interview?"
                            value={interview.interviewerName || ''}
                            onChange={(e) =>
                              setInterview((prev) => ({ ...prev, interviewerName: e.target.value }))
                            }
                          />
                        </div>
                        <div className="screening-form-group" style={{ flex: 1 }}>
                          <label className="screening-form-label">Interviewer Email</label>
                          <input
                            type="email"
                            className="screening-form-control"
                            placeholder="interviewer@company.com"
                            value={interview.interviewerEmail || ''}
                            onChange={(e) =>
                              setInterview((prev) => ({ ...prev, interviewerEmail: e.target.value }))
                            }
                          />
                        </div>
                      </div>
                      <div className="screening-form-group">
                        <label className="screening-form-label">Notes</label>
                        <textarea
                          className="screening-form-control"
                          rows={2}
                          value={interview.notes}
                          onChange={(e) =>
                            setInterview((prev) => ({ ...prev, notes: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <button className="btn-primary" type="submit" style={{ marginTop: 'auto' }}>
                      Schedule
                    </button>
                  </form>
                </div>



                {/* final outcome */}
                <div className="screening-dashboard-card" style={{ marginTop: '1.5rem' }}>
                  <h3>Final Outcome</h3>
                  <form onSubmit={handleSubmitOutcome} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1 }}>
                      <div className="screening-form-row" style={{ display: 'flex', gap: '0.75rem' }}>
                        <div className="screening-form-group" style={{ flex: 1 }}>
                          <label className="screening-form-label">Outcome *</label>
                          <select
                            className="screening-form-control"
                            value={outcome}
                            onChange={(e) => setOutcome(e.target.value)}
                            required
                          >
                            <option value="">Choose...</option>
                            <option value="ACCEPTED">Hire</option>
                            <option value="REJECTED">Do not hire</option>
                          </select>
                        </div>
                        <div className="screening-form-group" style={{ flex: 2 }}>
                          <label className="screening-form-label">Notes</label>
                          <input
                            type="text"
                            className="screening-form-control"
                            value={outcomeNotes}
                            onChange={(e) => setOutcomeNotes(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <button className="btn-primary" type="submit" style={{ marginTop: '1rem' }}>
                      Record Outcome
                    </button>
                  </form>
                </div>

              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default EmployerScreening;
